import React, { useCallback, useEffect, useState, useRef } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import {
  fetchAttendanceSource,
} from "../../../Apis/Attendance";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_attendance_source_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "attendance_source_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "attendance_source_name",
    label: "attendance_source_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "description",
    label: "description",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active",
    label: "is_active",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function AttendenceSourceList() {
  const [source, setSource] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_attendance_sources_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load attendance source data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_attendance_source_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/attendance-sources-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchAttendanceSource(org.organization_id);
          const a = oldData?.attendance_source;
          data = Array.isArray(a) ? a : [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        const b = data.map((item) => {
          return {
            ...item,
            id: item?.organization_attendance_source_id,
            is_active: item?.is_active == 1 ? "✔" : "✖",
          };
        });

        setSource(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Error fetching attendance sources:", error);
        toast.error("Failed to load attendance sources");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/attendance-source/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Attendance source deleted successfully");
          // Refresh data by updating search params
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Source";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete Attendance Source");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/attendance/source/edit/${item.id}`);
    },

    [navigate]
  );


         const handleShow = useCallback(
    (item) => {
      navigate(`/attendance/source/view/${item.id}`)
    },
    [navigate],
  )

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Attendance Source"}
        btnName={"Add Source"}
        delete_action={"ATTENDANCE_SOURCE_DELETE"}
        add_action={"ATTENDANCE_SOURCE_ADD"}
        Data={source}
        tableHeaders={[
          {
            name: "Source Name",
            value_key: "attendance_source_name",
            textStyle: "capitalize",
          },
          {
            name: "Description",
            value_key: "description",
            textStyle: "capitalize",
          },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Attendance Source",
          "Attendance Source",
          "Add Attendance Source",
          "Attendance Source",
        ]}
        Route={"/attendance/source"}
        setData={setSource}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_attendance_sources"
          primaryKey="organization_attendance_source_id"
          heading="Attendance Sources"
          data={source}
          sortname="attendance_source_name"
          showActions={true}
          Route="/attendance/source"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          edit_delete_action={["ATTENDANCE_SOURCE_DELETE", "ATTENDANCE_SOURCE_EDIT"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_attendance_sources"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default AttendenceSourceList;
